import { PlusIcon } from '@persona-id/design-tokens/icons';
import { FormError } from 'shared/components/FormControl';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { Team } from 'dashboard/models/team';
import { TeamsList } from 'dashboard/views/DashboardTeamsView/TeamsList';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface DashboardTeamsViewProps {
  teams: Team[] | undefined;
  teamsLoading: boolean;
  createTeam: (teamData: CreateTeamFormData) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
}

const DashboardTeamsView = ({
  teams,
  teamsLoading,
  createTeam,
  deleteTeam,
}: DashboardTeamsViewProps) => {
  const activeTeams = (teams ?? []).filter((team) => team?.attributes?.status === 'active');
  const [createTeamModal, { enable: showCreateTeamModal, disable: hideCreateTeamModal }] =
    useToggle(false);
  const handleCreateTeam = async (teamData: CreateTeamFormData) => {
    await createTeam(teamData);
    hideCreateTeamModal();
  };
  return (
    <>
      <Container>
        <Header title='Teams'>
          <Permissioned permission={Permission.TeamWrite}>
            <HStack>
              <Button
                id='teams-view-header__create-team-button'
                onClick={showCreateTeamModal}
                prefixIcon={<PlusIcon />}
              >
                Create team
              </Button>
            </HStack>
          </Permissioned>
        </Header>
        <Section>
          <TeamsList
            teams={activeTeams}
            isLoading={teamsLoading}
            showCreateTeamModal={showCreateTeamModal}
            deleteTeam={deleteTeam}
          />
        </Section>
      </Container>
      <ModalWrapper isOpen={createTeamModal} handleCloseModal={hideCreateTeamModal}>
        <ModalContainer>
          <ModalHeader onClose={hideCreateTeamModal}>Create new team</ModalHeader>
          <ModalBody>
            <FormCreateTeam handleCreateTeam={handleCreateTeam} cancel={hideCreateTeamModal} />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
    </>
  );
};

interface FormCreateTeamProps {
  handleCreateTeam: (teamData: CreateTeamFormData) => Promise<void>;
  cancel: () => void;
}

const FormCreateTeam = ({ handleCreateTeam, cancel }: FormCreateTeamProps) => {
  const {
    handleSubmit,
    register,
    formState: { isSubmitting, isValid, errors },
  } = useFormSecurely<{ name: string }>({
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(handleCreateTeam)}>
      <Label size='md' weight='bold'>
        Team name
      </Label>
      <Input
        {...register('name', {
          required: 'Teams must have a name.',
        })}
      />
      {errors.name && <FormError>{errors.name.message}</FormError>}
      <Spacer height={2} />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='create-team-modal__cancel'
          autoFocus={true}
          disabled={isSubmitting}
          onClick={cancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='primary'
          id='create-team-modal__confirm'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Create team
        </Button>
      </HStack>
    </form>
  );
};

export interface CreateTeamFormData {
  name: string;
}

export default DashboardTeamsView;
